import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CURRENT_YEAR = new Date().getFullYear();

function fmt(v) {
  if (v === null || v === undefined) return '';
  return Number.isInteger(v) ? v : v.toFixed(1);
}

// 반별 시수 현황: 학년/구분을 고르면, 그 학년에서 수업한 교육내용(지정과목)별로
// 각 반 재학생들의 시수(출석 인정 시수) 중 최대값을 학과/실기로 보여준다.
export default function ClassHours() {
  const { user } = useAuth();
  const isAdminStaff = user?.role === 'admin' || user?.role === 'staff';
  const [year, setYear] = useState(CURRENT_YEAR);
  const [grade, setGrade] = useState(1);
  const [classtype, setClasstype] = useState('M');
  // 보기: all=전체과목(그 학년에서 수업한 모든 교육내용), mine=담당과목(내가 수업한 교육내용만)
  // 행정직/관리자는 전체과목, 교사는 담당과목으로 시작한다.
  const [scope, setScope] = useState(isAdminStaff ? 'all' : 'mine');
  const [data, setData] = useState({ classes: [], rows: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/teacher/class-hours', { params: { year, grade, classtype, scope } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData({ classes: [], rows: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, grade, classtype, scope]);

  const classes = data.classes;

  // 같은 과목이 이어지는 구간의 첫 행에만 과목 칸을 두고, 과목 칸이 차지할 행 수(교육내용 수 x 2)를 센다.
  const subjectSpans = useMemo(() => {
    const spans = {};
    data.rows.forEach((r, i) => {
      if (i === 0 || data.rows[i - 1].subject !== r.subject) {
        let n = 1;
        while (i + n < data.rows.length && data.rows[i + n].subject === r.subject) n += 1;
        spans[i] = n * 2;
      }
    });
    return spans;
  }, [data.rows]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>반별 시수 현황</h2>
      </div>

      <div className="card">
        <div className="form-row hr-filter-row class-hours-filter">
          <div className="form-field form-field-narrow">
            <label>연도</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="M">면장반</option>
              <option value="R">정규반</option>
            </select>
          </div>
          <div className="form-field form-field-narrow class-hours-scope">
            <label>보기</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">전체과목 보기</option>
              <option value="mine">담당과목 보기</option>
            </select>
          </div>
        </div>

        <div className="class-hours-wrap">
          <table className="class-hours-table">
            <colgroup>
              <col className="ch-col-subject" />
              <col className="ch-col-chapter" />
              <col className="ch-col-type" />
              <col className="ch-col-legal" />
              {classes.map((c) => (
                <col key={c} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>과목</th>
                <th>교육내용</th>
                <th>구분</th>
                <th>기준</th>
                {classes.map((c) => (
                  <th key={c}>{c}반</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <React.Fragment key={r.amtcode}>
                  <tr className="ch-first">
                    {subjectSpans[i] && (
                      <td rowSpan={subjectSpans[i]} className="ch-subject">{r.subject}</td>
                    )}
                    <td rowSpan={2} className="ch-chapter">{r.chapter}</td>
                    <td>학과</td>
                    <td className="ch-legal">{fmt(r.legal_thrytime)}</td>
                    {classes.map((c) => (
                      <td key={c}>{fmt(r.classes[String(c)]?.thrytime)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>실기</td>
                    <td className="ch-legal">{fmt(r.legal_practime)}</td>
                    {classes.map((c) => (
                      <td key={c}>{fmt(r.classes[String(c)]?.practime)}</td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={4 + classes.length} className="empty-row">
                    {loading
                      ? '불러오는 중...'
                      : scope === 'mine'
                        ? '이 학년에서 내가 수업한 교육내용이 없습니다. "전체과목 보기"로 바꿔 보세요.'
                        : '이 학년에서 수업한 교육내용이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="form-message">
          반별 시수는 그 반 재학생 각각이 출석으로 인정받은 시수 중 최대값입니다. 해당 반에 그 교육내용의 출결이 없으면 빈 칸입니다.
        </div>
      </div>
    </div>
  );
}
